import { useLocation } from "wouter";
import { useI18n } from "@/lib/i18n";
import { trpc } from "@/lib/trpc";
import { PageChrome } from "@/components/tabs/PageChrome";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Loader2,
  Play,
} from "lucide-react";

/**
 * Admin Health page — single pane for Tier 1/2 monitoring + a few
 * safe maintenance actions. Server (`admin.health`) is the real
 * gate; this just renders. Auto-refreshes every 30s.
 */

const DOT: Record<string, string> = {
  ok: "bg-emerald-500",
  warn: "bg-amber-500",
  critical: "bg-red-600",
};
function StatusIcon({ s }: { s: string }) {
  if (s === "ok") return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
  if (s === "warn") return <AlertTriangle className="h-4 w-4 text-amber-500" />;
  return <XCircle className="h-4 w-4 text-red-600" />;
}

const JOBS = [
  "insights_sweep",
  "digest_daily",
  "freshness_reconcile",
  "manager_blocker_alert",
] as const;

// Web v1: the department-reports pipeline is pilot-locked (Phase-4
// redesign) — its manual trigger renders DISABLED with a controlled
// label, and the server (admin.runJob + the scheduled route) refuses
// it independently.
// RETIRED 2026-09-09 — the department-reports job is gone, so there is no
// locked job left to mark.
const LOCKED_JOBS = new Set<(typeof JOBS)[number]>([]);

export default function AdminHealth() {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const [, navigate] = useLocation();
  // ROUTE GUARD (Model-2 UI boundary): same provenance gate as AdminTeams.
  const orgQ = trpc.org.me.useQuery(undefined);
  const canManageOrg = !!(orgQ.data as any)?.canManageOrg;
  const q = trpc.admin.health.useQuery(undefined, {
    enabled: canManageOrg,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const runJob = trpc.admin.runJob.useMutation();
  const utils = trpc.useUtils();
  const d = q.data;

  async function trigger(job: (typeof JOBS)[number]) {
    try {
      const r = await runJob.mutateAsync({ job });
      toast[r.ok ? "success" : "error"](
        `${job}: ${r.ok ? "ok" : "failed"} (${r.status})`,
      );
      utils.admin.health.invalidate();
    } catch (e: any) {
      toast.error(e?.message ?? "failed");
    }
  }

  if (orgQ.isSuccess && !canManageOrg) {
    return (
      <PageChrome title={isAr ? "الإدارة" : "Administration"} backHref="/more">
        <div className="grid place-items-center p-10 text-center text-sm text-muted-foreground gap-3">
          <p>
            {isAr
              ? "لا توجد مؤسسة لإدارتها بهذا الحساب. مساحتك الشخصية خاصة بك ولا تُدار كشركة."
              : "There's no organization to administer with this account. Your Personal workspace is private and isn't managed like a company."}
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate("/")}>
            {isAr ? "العودة للرئيسية" : "Back to home"}
          </Button>
        </div>
      </PageChrome>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageChrome backHref="/more" backLabel={isAr ? "العودة" : "Back"}>
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-coral" />
          <h1 className="font-display text-2xl">
            {isAr ? "صحة النظام" : "System Health"}
          </h1>
          <button
            onClick={() => q.refetch()}
            className="ms-auto inline-flex items-center gap-1.5 text-xs rounded-full border border-border px-3 py-1.5 hover:bg-muted"
          >
            {q.isFetching ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {isAr ? "تحديث" : "Refresh"}
          </button>
        </div>

        {q.isLoading && (
          <div className="text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> …
          </div>
        )}
        {q.isError && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">
            {isAr ? "تعذّر التحميل (إداريّ فقط)" : "Couldn't load (admins only)."}
          </div>
        )}

        {d && (
          <>
            {/* Overall + checks */}
            <section className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <span
                  className={`h-3 w-3 rounded-full ${DOT[d.summary.overall] ?? "bg-muted"}`}
                />
                <span className="font-medium">
                  {isAr ? "الحالة العامة" : "Overall"}:{" "}
                  {d.summary.overall.toUpperCase()}
                </span>
                <span className="ms-auto text-[11px] text-muted-foreground">
                  {new Date(d.generatedAt).toLocaleString()}
                </span>
              </div>
              <ul className="divide-y divide-border/50">
                {d.summary.checks.map(c => (
                  <li
                    key={c.key}
                    className="flex items-center gap-3 py-2 text-sm"
                  >
                    <StatusIcon s={c.status} />
                    <span className="font-medium min-w-[180px]">{c.label}</span>
                    <span className="text-muted-foreground">{c.detail}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Runtime + Trial + Billing + LLM */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card title={isAr ? "وقت التشغيل" : "Runtime"}>
                <Row k="Uptime" v={`${Math.round(d.runtime.uptimeSec / 3600)}h`} />
                <Row k="Memory" v={`${d.runtime.memoryMB} MB`} />
                <Row k="Node" v={d.runtime.node} />
              </Card>
              <Card title={isAr ? "مقاييس التجربة (7 أيام)" : "Trial metrics (7d)"}>
                <Row k="Active users" v={String(d.trial.activeUsers)} />
                <Row k="Approved receipts" v={String(d.trial.approvedReceipts7d)} />
                <Row k="Signals" v={String(d.trial.signals7d)} />
                <Row
                  k="Priority completion"
                  v={`${d.trial.completionPct}% (${d.trial.prioritiesDone}/${d.trial.prioritiesTotal})`}
                />
                <Row
                  k="Insights new/actioned"
                  v={`${d.trial.insightsNew} / ${d.trial.insightsActioned}`}
                />
              </Card>
              <Card title={isAr ? "الفوترة والمقاعد" : "Billing & seats"}>
                <Row k="Plan" v={d.billing.plan ?? "—"} />
                <Row k="Billing status" v={d.billing.billingStatus ?? "active"} />
                <Row
                  k="Seats"
                  v={
                    d.billing.seatLimit == null
                      ? `${d.billing.seatUsed} (unlimited)`
                      : `${d.billing.seatUsed}/${d.billing.seatLimit}`
                  }
                />
              </Card>
              <Card title={isAr ? "ميزانية الذكاء الاصطناعي" : "LLM budget"}>
                <Row k="Today" v={`${d.llm.dayUsed}/${d.llm.dayCap}`} />
                <Row k="This minute" v={`${d.llm.minUsed}/${d.llm.minCap}`} />
              </Card>
            </div>

            {/* Maintenance actions */}
            <section className="rounded-2xl border border-border bg-card p-4">
              <div className="text-sm font-medium mb-2">
                {isAr ? "تشغيل مهمة الآن" : "Run a job now"}
              </div>
              <div className="flex flex-wrap gap-2">
                {JOBS.map(j =>
                  LOCKED_JOBS.has(j) ? (
                    <button
                      key={j}
                      disabled
                      data-testid={`job-locked-${j}`}
                      title={
                        isAr
                          ? "تقارير الأقسام مقفلة في الإصدار الأول (المرحلة الرابعة)."
                          : "Department reports are locked for Web v1 (Phase 4)."
                      }
                      className="inline-flex items-center gap-1.5 text-xs rounded-full border border-border px-3 py-1.5 opacity-50 cursor-not-allowed"
                    >
                      <Play className="h-3.5 w-3.5" />
                      {j} · {isAr ? "مقفل" : "locked"}
                    </button>
                  ) : (
                    <button
                      key={j}
                      onClick={() => trigger(j)}
                      disabled={runJob.isPending}
                      className="inline-flex items-center gap-1.5 text-xs rounded-full border border-border px-3 py-1.5 hover:bg-muted disabled:opacity-50"
                    >
                      {runJob.isPending &&
                      runJob.variables?.job === j ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Play className="h-3.5 w-3.5" />
                      )}
                      {j}
                    </button>
                  ),
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                {isAr
                  ? "يستدعي نفس نقطة النهاية المجدولة (آمن، خاضع للحدود)."
                  : "Triggers the same scheduled endpoint cron uses (safe, rate/budget-gated)."}
              </p>
            </section>
          </>
        )}
      </PageChrome>
    </div>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}
